import { bytesToUtf8, utf8ToBytes } from './encoding';

export const SAF_MAGIC = 'SAF1';
export const SAF_VERSION = 1;
export const SAF_CHUNK_SIZE = 1024 * 1024;
export const SAF_PBKDF2_ITERATIONS = 300_000;

export type SafHeader = {
  originalFilename: string;
  originalSize: number;
  chunkSize: number;
  kdf: {
    name: 'PBKDF2';
    hash: 'SHA-256';
    iterations: number;
    salt: string;
  };
  alg: 'AES-GCM';
  baseNonce: string;
  createdAt: string;
};

export function encodeUint32(value: number): Uint8Array {
  const buffer = new ArrayBuffer(4);
  const view = new DataView(buffer);
  view.setUint32(0, value, false);
  return new Uint8Array(buffer);
}

export function decodeUint32(bytes: Uint8Array): number {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  return view.getUint32(0, false);
}

export function encodeSafHeader(header: SafHeader): Uint8Array {
  return utf8ToBytes(JSON.stringify(header));
}

export function decodeSafHeader(bytes: Uint8Array): SafHeader {
  return JSON.parse(bytesToUtf8(bytes)) as SafHeader;
}

export function buildSafFileHeader(header: SafHeader): Uint8Array {
  const headerBytes = encodeSafHeader(header);
  const headerLength = headerBytes.length;
  const total = new Uint8Array(4 + 1 + 4 + headerLength);
  total.set(utf8ToBytes(SAF_MAGIC), 0);
  total[4] = SAF_VERSION;
  total.set(encodeUint32(headerLength), 5);
  total.set(headerBytes, 9);
  return total;
}

export function parseSafFileHeader(bytes: Uint8Array): {
  header: SafHeader;
  headerLength: number;
  headerOffset: number;
} {
  const magic = bytesToUtf8(bytes.slice(0, 4));
  if (magic !== SAF_MAGIC) {
    throw new Error('Invalid SAF header.');
  }
  const version = bytes[4];
  if (version !== SAF_VERSION) {
    throw new Error('Unsupported SAF version.');
  }
  const headerLength = decodeUint32(bytes.slice(5, 9));
  const headerStart = 9;
  const headerEnd = headerStart + headerLength;
  const header = decodeSafHeader(bytes.slice(headerStart, headerEnd));
  return { header, headerLength, headerOffset: headerEnd };
}

export function deriveChunkNonce(baseNonce: Uint8Array, index: number): Uint8Array {
  if (baseNonce.length !== 12) {
    throw new Error('Base nonce must be 12 bytes.');
  }
  const nonce = baseNonce.slice();
  const view = new DataView(nonce.buffer, nonce.byteOffset, nonce.byteLength);
  view.setUint32(8, index, false);
  return nonce;
}
